import { UserConfig } from "app/types";
import i18n from "features/shared/i18n";
import { AppUser, UserType } from "features/user/types";

const getLocaleLabel = (locale?: string | null): string => {
  switch (locale) {
    case "ja_kanji":
      return i18n.t("MSG_PARENT_PAGE_KANJI");
    case "en":
      return i18n.t("MSG_PARENT_PAGE_LANGUAGE_EN");
    case "zh_tw":
    case "zh_cn":
      return i18n.t("MSG_PARENT_PAGE_LANGUAGE_ZH_TW");
    case "ja":
    default:
      return i18n.t("MSG_PARENT_PAGE_HIRAGANA");
  }
};

interface SettingsSectionProps {
  selectedUser: AppUser;
  userConfig: UserConfig | undefined;
  onEditLanguage: () => void;
  onTogglePublish: () => void;
  onToggleCopy: () => void;
  onToggleAiChat: () => void;
  onToggleAiGeneration: () => void;
  onToggleShakeBugReport: () => void;
  onChangeUserStatus: () => void;
}

export function SettingsSection({
  selectedUser,
  userConfig,
  onEditLanguage,
  onTogglePublish,
  onToggleCopy,
  onToggleAiChat,
  onToggleAiGeneration,
  onToggleShakeBugReport,
  onChangeUserStatus,
}: SettingsSectionProps) {
  return (
    <div className="flex-col-el rounded-[12px] p-6">
      <div className="flex-row-el items-center gap-2 text-[24px] text-textcolor-black mb-5 pb-3 border-b border-outline">
        <span className="material-symbols-outlined !text-[45px] text-red-100">tune</span>
        {i18n.t("MSG_PARENT_PAGE_SETTINGS")}
      </div>
      <div className="flex-col-el">
        <div className="flex-row-el items-center gap-3 py-3 border-b border-outline overflow-hidden">
          <div className="w-10 h-10 rounded-[10px] flex items-center justify-center shrink-0 bg-purple-100/20">
            <span className="material-symbols-outlined text-[30px] text-purple-100">translate</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[18px] text-gray2-100 mb-0.5">
              {i18n.t("MSG_PARENT_PAGE_DISPLAY_LANGUAGE")}
            </div>
            <div className="text-[21px] text-textcolor-black">
              {getLocaleLabel(userConfig?.locale)}
            </div>
          </div>
          <button
            onClick={onEditLanguage}
            className="bg-white border border-gray2-60 text-textcolor-black rounded-full py-1.5 px-3 text-[18px] cursor-pointer hover:bg-gray2-20 transition-all"
          >
            {i18n.t("MSG_PARENT_PAGE_CHANGE")}
          </button>
        </div>
        <div className="flex-row-el items-center gap-3 py-3 border-b border-outline overflow-hidden">
          <div className="w-10 h-10 rounded-[10px] flex items-center justify-center shrink-0 bg-orange-100/20">
            <span className="material-symbols-outlined text-[30px] text-orange-100">public</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[18px] text-gray2-100 mb-0.5">
              {i18n.t("MSG_PARENT_PAGE_PROJECT_PUBLISH")}
            </div>
            <div
              className={`text-[21px] ${userConfig?.project_publish ? "text-green-100" : "text-gray2-100"}`}
            >
              {userConfig?.project_publish ? "ON" : "OFF"}
            </div>
          </div>
          <button
            onClick={onTogglePublish}
            className={`relative w-12 h-[26px] rounded-full cursor-pointer transition-colors shrink-0 border-none outline-none ${
              userConfig?.project_publish ? "bg-green-100" : "bg-gray2-100"
            }`}
          >
            <div
              className={`absolute top-0.5 left-0 w-[22px] h-[22px] bg-white rounded-full shadow transition-transform ${
                userConfig?.project_publish ? "translate-x-[24px]" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>
        <div className="flex-row-el items-center gap-3 py-3 border-b border-outline overflow-hidden">
          <div className="w-10 h-10 rounded-[10px] flex items-center justify-center shrink-0 bg-cyan-100/20">
            <span className="material-symbols-outlined text-[30px] text-cyan-100">
              content_copy
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[18px] text-gray2-100 mb-0.5">
              {i18n.t("MSG_PARENT_PAGE_COPY_PERMISSION")}
            </div>
            <div
              className={`text-[21px] ${userConfig?.project_copied ? "text-green-100" : "text-gray2-100"}`}
            >
              {userConfig?.project_copied ? "ON" : "OFF"}
            </div>
          </div>
          <button
            onClick={onToggleCopy}
            className={`relative w-12 h-[26px] rounded-full cursor-pointer transition-colors shrink-0 border-none outline-none ${
              userConfig?.project_copied ? "bg-green-100" : "bg-gray2-100"
            }`}
          >
            <div
              className={`absolute top-0.5 left-0 w-[22px] h-[22px] bg-white rounded-full shadow transition-transform ${
                userConfig?.project_copied ? "translate-x-[24px]" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>
        <div className="flex-row-el items-center gap-3 py-3 border-b border-outline overflow-hidden">
          <div className="w-10 h-10 rounded-[10px] flex items-center justify-center shrink-0 bg-teal-500/20">
            <span className="material-symbols-outlined text-[30px] text-teal-500">smart_toy</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[18px] text-gray2-100 mb-0.5">
              {i18n.t("MSG_PARENT_PAGE_AI_CHAT")}
            </div>
            <div
              className={`text-[21px] ${userConfig?.ai_chat ? "text-green-100" : "text-gray2-100"}`}
            >
              {userConfig?.ai_chat ? "ON" : "OFF"}
            </div>
          </div>
          <button
            onClick={onToggleAiChat}
            className={`relative w-12 h-[26px] rounded-full cursor-pointer transition-colors shrink-0 border-none outline-none ${
              userConfig?.ai_chat ? "bg-green-100" : "bg-gray2-100"
            }`}
          >
            <div
              className={`absolute top-0.5 left-0 w-[22px] h-[22px] bg-white rounded-full shadow transition-transform ${
                userConfig?.ai_chat ? "translate-x-[24px]" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>
        <div className="flex-row-el items-center gap-3 py-3 border-b border-outline overflow-hidden">
          <div className="w-10 h-10 rounded-[10px] flex items-center justify-center shrink-0 bg-violet-500/20">
            <span className="material-symbols-outlined text-[30px] text-violet-500">
              auto_awesome
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[18px] text-gray2-100 mb-0.5">
              {i18n.t("MSG_PARENT_PAGE_AI_GENERATION")}
            </div>
            <div
              className={`text-[21px] ${userConfig?.ai_generation ? "text-green-100" : "text-gray2-100"}`}
            >
              {userConfig?.ai_generation ? "ON" : "OFF"}
            </div>
          </div>
          <button
            onClick={onToggleAiGeneration}
            className={`relative w-12 h-[26px] rounded-full cursor-pointer transition-colors shrink-0 border-none outline-none ${
              userConfig?.ai_generation ? "bg-green-100" : "bg-gray2-100"
            }`}
          >
            <div
              className={`absolute top-0.5 left-0 w-[22px] h-[22px] bg-white rounded-full shadow transition-transform ${
                userConfig?.ai_generation ? "translate-x-[24px]" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>
        {/* 振る操作を検知できるのはアプリの中だけなので、web では出さない */}
        {window.ReactNativeWebView && (
          <div className="flex-row-el items-center gap-3 py-3 border-b border-outline overflow-hidden">
            <div className="w-10 h-10 rounded-[10px] flex items-center justify-center shrink-0 bg-blue-100/20">
              <span className="material-symbols-outlined text-[30px] text-blue-100">bug_report</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[18px] text-gray2-100 mb-0.5">
                {i18n.t("MSG_PARENT_PAGE_SHAKE_BUG_REPORT")}
              </div>
              <div
                className={`text-[21px] ${userConfig?.shake_bug_report ? "text-green-100" : "text-gray2-100"}`}
              >
                {userConfig?.shake_bug_report ? "ON" : "OFF"}
              </div>
            </div>
            <button
              onClick={onToggleShakeBugReport}
              className={`relative w-12 h-[26px] rounded-full cursor-pointer transition-colors shrink-0 border-none outline-none ${
                userConfig?.shake_bug_report ? "bg-green-100" : "bg-gray2-100"
              }`}
            >
              <div
                className={`absolute top-0.5 left-0 w-[22px] h-[22px] bg-white rounded-full shadow transition-transform ${
                  userConfig?.shake_bug_report ? "translate-x-[24px]" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
        )}
        <div className="flex-row-el items-center gap-3 py-3">
          <div
            className={`w-10 h-10 rounded-[10px] flex items-center justify-center shrink-0 ${
              selectedUser.type !== UserType.FROZEN ? "bg-green-60/20" : "bg-danger/20"
            }`}
          >
            <span
              className={`material-symbols-outlined text-[30px] ${
                selectedUser.type !== UserType.FROZEN ? "text-green-100" : "text-danger"
              }`}
            >
              {selectedUser.type !== UserType.FROZEN ? "check_circle" : "block"}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[18px] text-gray2-100 mb-0.5">
              {i18n.t("MSG_PARENT_PAGE_ACCOUNT_STATUS")}
            </div>
            <div
              className={`text-[21px] ${
                selectedUser.type !== UserType.FROZEN ? "text-green-100" : "text-danger"
              }`}
            >
              {selectedUser.type !== UserType.FROZEN
                ? i18n.t("MSG_PARENT_PAGE_PLAYABLE")
                : i18n.t("MSG_PARENT_PAGE_SUSPENDED")}
            </div>
          </div>
          <button
            onClick={onChangeUserStatus}
            className={`relative w-12 h-[26px] rounded-full cursor-pointer transition-colors shrink-0 border-none outline-none ${
              selectedUser.type !== UserType.FROZEN ? "bg-green-100" : "bg-gray2-100"
            }`}
          >
            <div
              className={`absolute top-0.5 left-0 w-[22px] h-[22px] bg-white rounded-full shadow transition-transform ${
                selectedUser.type !== UserType.FROZEN ? "translate-x-[24px]" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
}
